// useFinanceStream — thin React adapter over the `financeStream` singleton
// (apps/web/backoffice/src/lib/finance-stream.ts). The singleton owns the
// WebSocket lifetime; this hook only subscribes/unsubscribes on mount and
// dispatches events to React Query + the notifications store + Sonner.
//
// Why a singleton: AppLayout re-renders on every route change (Breadcrumbs
// reads useLocation) and StrictMode double-mounts in dev. A hook-owned
// WebSocket would tear down and reconnect each time, leaking sockets and
// showing duplicate toasts. The singleton makes the connection persist for
// the lifetime of the session — opened once when the access token appears,
// closed only on logout (`financeStream.stop()` in auth-store).
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { queueNotificationToast } from '@/lib/toast-queue';
import { useAuthStore } from '@/stores/auth-store';
import {
  useNotificationsStore,
  type FinanceNotificationKind,
} from '@/stores/notifications-store';
import { financeStream, type FinanceStreamEvent } from '@/lib/finance-stream';
import { formatMoneyMinor, shortId } from '@/lib/finance-format';
import { playNotifySound } from '@/lib/notify-sound';
import { notifyDesktop } from '@/lib/desktop-notify';

const TAB_FOR: Record<FinanceNotificationKind, 'deposits' | 'withdraws' | 'member-banks'> = {
  'deposit.created': 'deposits',
  'deposit.updated': 'deposits',
  'withdraw.created': 'withdraws',
  'withdraw.updated': 'withdraws',
  'bank_account.created': 'member-banks',
  'bank_account.updated': 'member-banks',
};

/**
 * Which stream events are worth interrupting an operator for.
 *
 * `*.updated` on a deposit/withdraw is a status change an admin just made —
 * toasting it echoes the operator's own approve/reject back at them, and a
 * withdraw approval emits two of them (approved, then completed after payout).
 * The table still refreshes via query invalidation; only new items, and bank
 * accounts a member left unverified, need a toast.
 */
export function shouldToast(event: FinanceStreamEvent): boolean {
  switch (event.event) {
    case 'deposit.created':
    case 'withdraw.created':
    case 'bank_account.created':
      return true;
    case 'bank_account.updated':
      return event.verified !== true;
    default:
      return false;
  }
}

export function useFinanceStream(): void {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const addNotification = useNotificationsStore((s) => s.add);

  // Refs hold the latest renderer-bound values so the subscription effect
  // doesn't need them in its dep array — keeps the subscription stable.
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);
  tRef.current = t;
  navigateRef.current = navigate;

  // 1) Drive the singleton from the auth token. start() is idempotent for
  //    the same token, so a re-render with an unchanged token is a no-op.
  useEffect(() => {
    if (!accessToken) return;
    financeStream.start(accessToken);
    // Do NOT call stop() on unmount — the singleton outlives this component.
    // Logout drives stop() through auth-store.
  }, [accessToken]);

  // 2) Subscribe to events; dispatch to React Query + store + toast.
  useEffect(() => {
    if (!accessToken) return;
    const unsub = financeStream.subscribe((data: FinanceStreamEvent) => {
      const kind = data.event;

      if (kind === 'deposit.created' || kind === 'deposit.updated') {
        qc.invalidateQueries({ queryKey: ['/api/v1/deposits'] });
      } else if (kind === 'withdraw.created' || kind === 'withdraw.updated') {
        qc.invalidateQueries({ queryKey: ['/api/v1/withdraws'] });
      } else if (kind === 'bank_account.created' || kind === 'bank_account.updated') {
        qc.invalidateQueries({ queryKey: ['/api/v1/members/bank-accounts'] });
        if (!shouldToast(data)) return;
        const tr = tRef.current;
        const titleKey =
          kind === 'bank_account.created'
            ? 'finance.notifications.bankAccountCreated'
            : 'finance.notifications.bankAccountReverify';
        const masked = data.accountNumber
          ? `****${data.accountNumber.slice(-4)}`
          : undefined;
        const description = [data.username, masked].filter(Boolean).join(' · ');
        addNotification({
          kind,
          id: data.id,
          agentId: data.agentId,
          memberId: data.memberId,
          username: data.username,
          accountNumber: data.accountNumber,
          receivedAt: Date.now(),
        });
        queueNotificationToast(tr(titleKey), {
          description: description || undefined,
          duration: 8000,
          action: {
            label: tr('finance.notifications.view'),
            onClick: () => navigateRef.current('/finance?tab=member-banks'),
          },
        });
        return;
      } else {
        return;
      }

      if (!shouldToast(data)) return;

      // A new deposit/withdraw request just arrived — audible alert.
      playNotifySound();

      addNotification({
        kind,
        id: data.id,
        status: data.status,
        agentId: data.agentId,
        memberId: data.memberId,
        amount: data.amount,
        receivedAt: Date.now(),
      });
      const tab = TAB_FOR[kind];
      const tr = tRef.current;
      const titleKey =
        kind === 'deposit.created'
          ? 'finance.notifications.depositCreated'
          : 'finance.notifications.withdrawCreated';
      const amountText = formatMoneyMinor(data.amount, 'THB');
      const title = amountText ? `${tr(titleKey)} · ${amountText}` : tr(titleKey);

      const parts: string[] = [];
      if (data.status) {
        parts.push(tr(`finance.status.${data.status}`, data.status));
      }
      if (data.memberId) {
        parts.push(`${tr('finance.notifications.member')} ${shortId(data.memberId)}`);
      }
      if (data.id) parts.push(`#${shortId(data.id)}`);

      queueNotificationToast(title, {
        description: parts.length > 0 ? parts.join(' · ') : undefined,
        duration: 8000,
        action: {
          label: tr('finance.notifications.view'),
          onClick: () => navigateRef.current(`/finance?tab=${tab}`),
        },
      });

      // Surface above other apps when the BO tab is hidden (minimized / other
      // app); no-op while the tab is visible — the toast covers that.
      notifyDesktop(title, parts.join(' · '), () => navigateRef.current(`/finance?tab=${tab}`));
    });

    const unsubError = financeStream.onError(() => {
      qc.invalidateQueries({ queryKey: ['/api/v1/deposits'] });
      qc.invalidateQueries({ queryKey: ['/api/v1/withdraws'] });
    });

    return () => {
      unsub();
      unsubError();
    };
  }, [accessToken, qc, addNotification]);
}
